import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    CashPlus,
    ClassicPortfolio,
    PremiumPortfolio,
} from '@/components/common/icons';
import { Paragraph, Text } from '@/components/common/Typography';
import { FlexProps } from '@chakra-ui/react';

const LearnMoreLink = ({ href }: { href: string }) => (
    <ArrowLinkText fontSize="md" href={href} target="_blank">
        Learn more
    </ArrowLinkText>
);

interface DescriptionProps extends FlexProps {
    text: string;
    href: string;
    displayArrowLinkInline?: boolean;
}

const Description = ({
    text,
    href,
    displayArrowLinkInline = false,
    ...props
}: DescriptionProps) => (
    <FlexCol alignItems="flex-start" {...props}>
        <Paragraph color="textMuted">
            {text}
            {displayArrowLinkInline && (
                <>
                    &nbsp;
                    <LearnMoreLink href={href} />
                </>
            )}
        </Paragraph>
        {!displayArrowLinkInline && <LearnMoreLink href={href} />}
    </FlexCol>
);

export const CashPlusLabel = (props: FlexProps) => (
    <FlexRow alignItems="center" {...props}>
        <CashPlus size="sm" mr={2} />
        <Text fontWeight="medium">Cash +</Text>
    </FlexRow>
);

interface DescriptionComponentProps extends FlexProps {
    displayArrowLinkInline?: boolean;
}
export const CashPlusDescription = ({
    displayArrowLinkInline,
    ...props
}: DescriptionComponentProps) => (
    <Description
        text="Plenty’s Cash+ portfolio is not a savings account. It’s a portfolio that’s 99.5%+ invested in US-government backed treasuries. We believe this is the lowest risk place for your cash. Unlike CDs or bonds, you can withdraw any time without a penalty."
        href="https://www.withplenty.com/blog/how-to-earn-more-on-your-cash"
        displayArrowLinkInline={displayArrowLinkInline}
        {...props}
    />
);

export const PremiumPortfolioLabel = (props: FlexProps) => (
    <FlexRow alignItems="center" {...props}>
        <PremiumPortfolio size="sm" mr={2} />
        <Text fontWeight="medium">Premium portfolio</Text>
    </FlexRow>
);

export const PremiumPortfolioDescription = ({
    displayArrowLinkInline,
    ...props
}: DescriptionComponentProps) => (
    <Description
        text="Our premium portfolios use individual stocks and bonds to construct a diversified portfolio, using a strategy called direct indexing. Direct indexing is a decades-old strategy used by the wealthy to invest while reducing future capital gains taxes through tax loss harvesting."
        href="https://www.withplenty.com/blog/direct-indexing-the-investing-strategy-of-the-wealthy"
        displayArrowLinkInline={displayArrowLinkInline}
        {...props}
    />
);

export const ClassicPortfolioLabel = (props: FlexProps) => (
    <FlexRow alignItems="center" {...props}>
        <ClassicPortfolio size="sm" mr={2} />
        <Text fontWeight="medium">Classic portfolio</Text>
    </FlexRow>
);

export const ClassicPortfolioDescription = ({
    displayArrowLinkInline,
    ...props
}: DescriptionComponentProps) => (
    <Description
        text="Our classic portfolios use ETFs to construct a diversified portfolio across stocks and bonds. These are straightforward portfolios that give you exposure into US stocks, International stocks, bonds, and treasuries. Tax loss harvesting is not included."
        href="https://www.withplenty.com/blog/what-is-modern-portfolio-theory-and-how-are-plenty-portfolios-constructed"
        displayArrowLinkInline={displayArrowLinkInline}
        {...props}
    />
);
